'use client';

import { c as _c } from "react/compiler-runtime";
import checkGreen from '@images/svg/check-green.svg';
import { RESERVED_FLATS_31832 } from '@lib/flags';
import { activeBulkSelector } from '@lib/store/slices/VisualBulkSlice';
import { activeFlatSelector, activeFloorSelector } from '@lib/store/slices/VisualFlatSlice';
import { activeQueueSelector } from '@lib/store/slices/VisualSlice';
import changeDate from '@lib/tools/_change_date';
import { useParams } from 'next/navigation';
import React, { useMemo } from 'react';
import Svg from 'react-inlinesvg';
import { useSelector } from 'react-redux';
import { isCompletedQueue } from '../../constants';
import styles from './VisualHintInfo.module.scss';
const STUDIO_NAME = 'СТ';
const STAGE_NUMBER = {
  1: 'I',
  2: 'II'
};
const compareStudioFirst = (left, right) => {
  const leftIsStudio = left.rooms === STUDIO_NAME;
  const rightIsStudio = right.rooms === STUDIO_NAME;
  if (leftIsStudio) return -1;
  if (rightIsStudio) return 1;
  return 0;
};
const FLOORS_NAME = {
  one: 'этаж',
  few: 'этажа',
  many: 'этажей'
};
const pluralRules = new Intl.PluralRules('ru-RU');
const formatFloors = count => count ? `${count} ${FLOORS_NAME[pluralRules.select(count)]}` : '';
const RoomsList = t0 => {
  const $ = _c(6);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    let t2;
    if ($[2] !== items.length) {
      t2 = (item, index) => <React.Fragment key={item.rooms}><div className={styles.itemInfo}><div className={styles.itemInfoRooms}>{item.rooms}</div><div className={styles.itemInfoFlat}><p className={styles.itemInfoFlatCount}>{item.count} квартир</p><p className={styles.itemInfoFlatPrice}>От {item.minPrice} ₽</p></div></div>{items.length - 1 !== index && <div className={styles.crosslineItemInfo} />}</React.Fragment>;
      $[2] = items.length;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = items.map(t2);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <div>{t1}</div>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const QueueHeader = t0 => {
  const $ = _c(13);
  const {
    queue,
    date
  } = t0;
  let t1;
  if ($[0] !== queue) {
    t1 = isCompletedQueue(queue);
    $[0] = queue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isCompleted = t1;
  const t2 = STAGE_NUMBER[queue];
  let t3;
  if ($[2] !== t2) {
    t3 = <p className={styles.itemTitle}>{t2} очередь</p>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `${styles.itemDescription} ${isCompleted ? styles.completed : ""}`;
  let t5;
  if ($[4] !== date || $[5] !== isCompleted) {
    t5 = isCompleted ? <><Svg src={checkGreen.src} /><span>Дом сдан</span></> : <><span>Ключи до</span><span>{changeDate(date, "")}</span></>;
    $[4] = date;
    $[5] = isCompleted;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <p className={t4}>{t5}</p>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
const BulkHeader = t0 => {
  const $ = _c(10);
  const {
    bulkNumber,
    maxFloor
  } = t0;
  let t1;
  if ($[0] !== bulkNumber) {
    t1 = <p className={styles.itemTitle}>Корпус {bulkNumber}</p>;
    $[0] = bulkNumber;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Этажность</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== maxFloor) {
    t3 = formatFloors(maxFloor);
    $[3] = maxFloor;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <p className={styles.itemDescription}>{t2}<span>{t3}</span></p>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <>{t1}{t4}</>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const FloorHeader = t0 => {
  const $ = _c(2);
  const {
    floor
  } = t0;
  let t1;
  if ($[0] !== floor) {
    t1 = <p className={styles.itemTitle}>Этаж {floor}</p>;
    $[0] = floor;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FlatHeader = t0 => {
  const $ = _c(15);
  const {
    number,
    area,
    price
  } = t0;
  let t1;
  if ($[0] !== number) {
    t1 = <p className={styles.itemTitleFlat}>Квартира №{number}</p>;
    $[0] = number;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Площадь</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <sup>2</sup>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== area) {
    t4 = <p className={styles.itemDescriptionFlat}>{t2}<span>{area} м{t3}</span></p>;
    $[4] = area;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={styles.crossline} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t4) {
    t6 = <div>{t1}{t4}{t5}</div>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== price) {
    t7 = <p className={styles.itemTitle}>{price} ₽</p>;
    $[10] = price;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
const ReservedFlatHeader = t0 => {
  const $ = _c(14);
  const {
    number,
    area
  } = t0;
  let t1;
  if ($[0] !== number) {
    t1 = <p className={styles.itemTitleFlat}>Квартира №{number}</p>;
    $[0] = number;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Площадь</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <sup>2</sup>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== area) {
    t4 = <p className={styles.itemDescriptionFlat}>{t2}<span>{area} м{t3}</span></p>;
    $[4] = area;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={styles.crossline} />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t4) {
    t6 = <div>{t1}{t4}{t5}</div>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className={styles.reservedItemTitle}>Квартира забронирована</p>;
    t8 = <p className={styles.reservedItemDesc}>Посмотрите похожие варианты или подпишитесь на эту квартиру — уведомим вас если квартира освободится.</p>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6) {
    t9 = <>{t6}{t7}{t8}</>;
    $[12] = t6;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
};
const VisualHintInfo = t0 => {
  const $ = _c(54);
  const {
    type,
    flats,
    bulkIds: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const bulkIds = t2;
  const {
    bulkId,
    floorId
  } = useParams();
  const activeQueue = useSelector(activeQueueSelector);
  const activeBulk = useSelector(activeBulkSelector);
  const activeFloor = useSelector(activeFloorSelector);
  const activeFlat = useSelector(activeFlatSelector);
  let t3;
  if ($[2] !== activeBulk || $[3] !== bulkId) {
    t3 = Number(activeBulk?.split("_").slice(2)?.join("_")) || +bulkId;
    $[2] = activeBulk;
    $[3] = bulkId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const activeBulkId = t3;
  let t4;
  if ($[5] !== activeBulkId || $[6] !== flats) {
    t4 = flats?.find(flat => flat.bulk.id === activeBulkId);
    $[5] = activeBulkId;
    $[6] = flats;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const currentBulk = t4;
  let t5;
  if ($[8] !== activeBulkId || $[9] !== activeFlat || $[10] !== activeFloor || $[11] !== bulkIds || $[12] !== flats || $[13] !== floorId || $[14] !== type) {
    let t6;
    if ($[16] !== activeBulkId || $[17] !== activeFlat || $[18] !== activeFloor || $[19] !== bulkIds || $[20] !== floorId || $[21] !== type) {
      t6 = flat_0 => {
        switch (type) {
          case "queue":
          case "bulks":
            {
              return bulkIds.includes(flat_0.bulk.id);
            }
          case "floor":
            {
              return flat_0.bulk.id === activeBulkId && flat_0.floor === (activeFloor ?? +floorId);
            }
          case "flat":
            {
              return flat_0.bulk.id === activeBulkId && flat_0.floor === (activeFloor ?? +floorId) && flat_0.number === String(activeFlat);
            }
          default:
            {
              return true;
            }
        }
      };
      $[16] = activeBulkId;
      $[17] = activeFlat;
      $[18] = activeFloor;
      $[19] = bulkIds;
      $[20] = floorId;
      $[21] = type;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    t5 = flats.filter(t6);
    $[8] = activeBulkId;
    $[9] = activeFlat;
    $[10] = activeFloor;
    $[11] = bulkIds;
    $[12] = flats;
    $[13] = floorId;
    $[14] = type;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const filteredFlats = t5;
  let t6;
  if ($[23] !== filteredFlats) {
    t6 = filteredFlats.reduce(_temp, {});
    $[23] = filteredFlats;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const roomsInfo = t6;
  let t7;
  if ($[25] !== roomsInfo) {
    t7 = Object.entries(roomsInfo).map(_temp2).sort(compareStudioFirst);
    $[25] = roomsInfo;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const roomsInfoArray = t7;
  const settlementQueueDate = filteredFlats.length ? filteredFlats[0].bulk.keyDate : "";
  const currentFlat = type === "flat" ? filteredFlats?.[0] : null;
  const isReservedFlat = RESERVED_FLATS_31832 && currentFlat?.status === "reserve";
  let t8;
  if ($[27] !== activeQueue || $[28] !== settlementQueueDate) {
    t8 = <QueueHeader queue={activeQueue} date={settlementQueueDate} />;
    $[27] = activeQueue;
    $[28] = settlementQueueDate;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const t9 = currentBulk?.bulk?.number;
  const t10 = currentBulk?.section?.floorsCount;
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <BulkHeader bulkNumber={t9} maxFloor={t10} />;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== activeFloor) {
    t12 = <FloorHeader floor={activeFloor} />;
    $[33] = activeFloor;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== filteredFlats[0]?.area || $[36] !== isReservedFlat || $[37] !== roomsInfoArray[0]?.minPrice || $[38] !== roomsInfoArray[0]?.number) {
    t13 = isReservedFlat ? <ReservedFlatHeader number={roomsInfoArray[0]?.number} area={filteredFlats[0]?.area} /> : <FlatHeader number={roomsInfoArray[0]?.number} area={filteredFlats[0]?.area} price={roomsInfoArray[0]?.minPrice} />;
    $[35] = filteredFlats[0]?.area;
    $[36] = isReservedFlat;
    $[37] = roomsInfoArray[0]?.minPrice;
    $[38] = roomsInfoArray[0]?.number;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t8) {
    t14 = {
      queue: t8,
      bulks: t11,
      floor: t12,
      flat: t13
    };
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t8;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  const headers = t14;
  const t15 = headers[type];
  let t16;
  if ($[45] !== t15) {
    t16 = <header className={styles.header}>{t15}</header>;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== filteredFlats.length || $[48] !== roomsInfoArray || $[49] !== type) {
    t17 = type !== "flat" && <><div className={styles.crossline} /><div className={styles.flatCount}>{filteredFlats.length} квартир в продаже</div><RoomsList items={roomsInfoArray} /></>;
    $[47] = filteredFlats.length;
    $[48] = roomsInfoArray;
    $[49] = type;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t16 || $[52] !== t17) {
    t18 = <div className={styles.root}>{t16}{t17}</div>;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  return t18;
};
export default VisualHintInfo;
function _temp(acc, flat_1) {
  if (!flat_1.price) {
    return acc;
  }
  const priceNum = typeof flat_1.price === "string" ? Number(flat_1.price.replace(/\s+/g, "")) : flat_1.price;
  if (!acc[flat_1.rooms]) {
    acc[flat_1.rooms] = {
      count: 0,
      minPriceNum: priceNum,
      minPriceText: flat_1.textPrice ?? flat_1.price,
      area: flat_1.area,
      number: flat_1.number
    };
  }
  acc[flat_1.rooms].count = acc[flat_1.rooms].count + 1;
  if (priceNum < acc[flat_1.rooms].minPriceNum) {
    acc[flat_1.rooms].minPriceNum = priceNum;
    acc[flat_1.rooms].minPriceText = flat_1.textPrice ?? flat_1.price;
    acc[flat_1.rooms].area = flat_1.area;
    acc[flat_1.rooms].number = flat_1.number;
  }
  return acc;
}
function _temp2(t0) {
  const [rooms, data] = t0;
  return {
    rooms: rooms === "studio" ? STUDIO_NAME : `${rooms}C`,
    count: data.count,
    minPrice: data.minPriceText,
    area: data.area,
    number: data.number
  };
}